import { FC, useState, useEffect, useRef } from "react";
import { Switch } from 'antd';
import { Button } from 'elements';

import Option, { IOption } from './Option';

import { items as data } from "../../data";

// @ts-ignore
// import Missionchip from "atoms/MissionElement/Missionchip/Missionchip";

// @ts-ignore
import Stars from "assets/svg/stars.svg";

// @ts-ignore
import ReLoad from "assets/svg/reload.svg";

// @ts-ignore
import MenuExpandLeft from "assets/svg/MenuExpandLeft.svg";

// @ts-ignore
import assistantLogo from "assets/images/assistant-logo.svg";

// @ts-ignore
import loaderIcon from "assets/images/assistant-loader.gif";

import {
  MainWrapper,
  Body,

  AiSuggestionContianer,
  Header,
  CloseBtn,
  HeaderWrap,
  HeaderTitle,
  SubHeader,
  SubHeaderTitle,
  ChipsWrapper,
  Footer,
  Image,
  RegGernerateButton,
  Wrapper,

  // Loader
  LoaderWrapper,
  LoaderBody,
  LoaderText,
  LoaderImg,
  LoaderError
} from "./styled";

import { websocketConnection } from "utils/websocket";

enum DataType {
  ROLES = 1,
  VALUES = 2,
  PRINCIPLES = 3,
  CODE = 4,
  PURPOSE = 5,
  VISION = 6,
}

interface IGoalData {
  category: string;
  area: string;
  domain: string;
}

interface IRole {
  label: string;
}

interface IValue {
  label: string;
}

interface IMissionStatement {
  roles: IRole[];
  values: IValue[];
  principle: { name: string };
  code_of_conduct: { name: string };
  purpose: { name: string };
  vision: { name: string };
}

interface IPromps {
  principle: string;
  code_of_conduct: string;
  purpose: string;
  vision: string;
}

interface IProps {
  step: number;
  goal: IGoalData;
  missionStatement: IMissionStatement;
  prompts: IPromps;
  onSelect: (items: IOption[], dataType: number) => void;
  onClose: () => void;
}

interface IItem {
  id: string;
  text: string;
}

const getDataType = (step: number): DataType => {
  const stepMapping: Record<number, DataType> = {
    1: DataType.ROLES,
    2: DataType.VALUES,
    3: DataType.PRINCIPLES,
    4: DataType.CODE,
    5: DataType.PURPOSE,
    6: DataType.VISION,
  };

  return stepMapping[step];
}

function randomID(): string {
  return `id_` + Math.floor(10000 + Math.random() * 90000);
}

const increaseBy = (num: number) => {
  let counter = num;

  return () => {
    const result = counter;
    counter += 50;
    return result;
  };
}

// Automatic retry: reconnect and resend a pending request a limited number of
// times so a flaky connection recovers on its own without turning into an
// endless connect/disconnect loop.
const RETRY_DELAY = 5000;
const MAX_AUTO_RETRIES = 5;

interface IRenderLoader {
  title: string;
  loading: boolean;
  error?: string;
}

const Loader: FC<IRenderLoader> = ({ title, loading, error }) => {
  const animateDelayCounter = increaseBy(100);

  return (
    <LoaderWrapper>
      <LoaderBody>
        <LoaderImg
          src={assistantLogo}
          width={69}
          height={69}
          $animate={loading}
          $animateDelay={animateDelayCounter()}
          alt=""
        />
        <LoaderText
          $animate={loading}
          $animateDelay={animateDelayCounter()}
        >
          Suggesting {title}...
        </LoaderText>
        <LoaderImg
          src={loaderIcon}
          width={52}
          height={53}
          $animate={loading}
          $animateDelay={animateDelayCounter()}
          alt="Loading"
        />
      </LoaderBody>

      {error && (
        <LoaderError
          $animate={!!error}
          $animateDelay={animateDelayCounter()}
        >
          {error}
        </LoaderError>
      )}
    </LoaderWrapper>
  );
};

const AIHelper: FC<IProps> = ({ step, goal, missionStatement, prompts, onSelect, onClose }) => {
  const [isSwitch, setIsSwitch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [items, setItems] = useState<IItem[]>([]);
  const [isShowMore, setIsShowMore] = useState(false);

  const [selectedItems, setSelectedItems] = useState<IOption[]>([]);

  const dataTypeRef = useRef<DataType>(getDataType(step));
  const dataType = getDataType(step);

  // Guards against duplicate in-flight suggestion requests. A second request
  // fired while one is still pending would let its response overwrite the
  // suggestions the user is already looking at.
  const pendingRequestRef = useRef(false);

  // Tracks whether the user is currently seeing a rendered list of suggestions
  // (as opposed to a loader). Used from websocket callbacks, which can be stale
  // closures, so it must be a ref rather than reading `items`/`loading` state.
  const suggestionsShownRef = useRef(false);

  // Tracks whether a suggestion request is awaiting a response. It is used to
  // know when to surface a connection error and to automatically resend the
  // request once the connection is re-established.
  const isPendingRef = useRef(false);

  // Automatic retry bookkeeping (see MAX_AUTO_RETRIES above).
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  const clearRetry = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = () => {
    clearRetry();

    // Don't retry forever; after the limit keep the error message on screen.
    if (retryCountRef.current >= MAX_AUTO_RETRIES) {
      return;
    }

    retryCountRef.current += 1;

    retryTimerRef.current = setTimeout(handleRefresh, RETRY_DELAY);
  };

  useEffect(() => {
    if (isSwitch) {
      setLoading(true);
      setError(false);
      setErrorMessage("");
    }

    isPendingRef.current = isSwitch;
    retryCountRef.current = 0;

    websocketConnection.connect(handleOpen, handleError, handleErrorClose);
    websocketConnection.setMessageCallback(handleMessage);

    if (!isSwitch) {
      setItems([]);
      setIsShowMore(false);
      suggestionsShownRef.current = false;
    }

    return () => {
      clearRetry();
      websocketConnection.close();
    };
  }, [step, isSwitch]);

  useEffect(() => {
    if (dataTypeRef.current !== dataType) {
      dataTypeRef.current = dataType;
    }
  }, [dataType]);

  const handleSwitchToggle = () => {
    setIsSwitch(prev => !prev);
  };

  const handleSuggest = () => {
    // Skip duplicate requests while one is already in flight so a second
    // response can't replace the currently visible suggestions.
    if (pendingRequestRef.current) {
      return;
    }

    pendingRequestRef.current = true;

    const endpointMapping: Record<DataType, string> = {
      [DataType.ROLES]: "suggest_roles",
      [DataType.VALUES]: "suggest_values",
      [DataType.PRINCIPLES]: "suggest_principles",
      [DataType.CODE]: "suggest_code_of_conduct",
      [DataType.PURPOSE]: "suggest_purpose",
      [DataType.VISION]: "suggest_vision",
    };

    const getEndpoint = () => {
      const baseData = {
        domain: goal?.domain,
        area: goal?.area,
        category: goal?.category
      };
      
      const endpoint = endpointMapping[dataType];
      let additionalData = {};

      if (dataType >= DataType.ROLES) {
        additionalData = { ...additionalData, previous_suggested_roles: missionStatement.roles.map((role) => role.label) };
      }

      if (dataType >= DataType.VALUES) {
        additionalData = { ...additionalData, role: missionStatement.roles[0]?.label, previous_suggested_values: missionStatement.values.map((value) => value.label) };
      }

      if (dataType >= DataType.PRINCIPLES) {
        additionalData = { ...additionalData, value: missionStatement.values[0]?.label, auto_completion_text: prompts.principle, previous_suggested_principles: missionStatement.principle?.name };
      }

      if (dataType >= DataType.CODE) {
        additionalData = { ...additionalData, principle: missionStatement.principle?.name, auto_completion_text: prompts.code_of_conduct, previous_suggested_codes: missionStatement.code_of_conduct?.name };
      }

      if (dataType >= DataType.PURPOSE) {
        additionalData = { ...additionalData, code_of_conduct: missionStatement.code_of_conduct?.name, auto_completion_text: prompts.purpose, previous_suggested_purposes: missionStatement.purpose?.name };
      }

      if (dataType >= DataType.VISION) {
        additionalData = { ...additionalData, purpose: missionStatement.purpose?.name, auto_completion_text: prompts.vision, previous_suggested_visions: missionStatement.vision?.name };
      }

      return {
        endpoint,
        data: { ...baseData, ...additionalData },
      };
    };

    isPendingRef.current = true;
    websocketConnection.send(JSON.stringify(getEndpoint()));
  };

  const handleOpen = () => {
    // Called on every successful (re)connection. When there is a request
    // awaiting a response, resend it automatically so the user doesn't have to
    // manually refresh. The error label stays visible until data arrives.
    clearRetry();
    retryCountRef.current = 0;

    if (isPendingRef.current) {
      // Clear the pending guard so handleSuggest can resend even if a
      // previous request was left marked as pending.
      pendingRequestRef.current = false;
      setLoading(true);
      handleSuggest();
    }
  };

  const handleErrorClose = () => {
    // Only surface a connection error while suggestions are enabled.
    if (!isSwitch) return;

    pendingRequestRef.current = false;
    // Stop the utility's own reconnect loop so we stay in control of retries.
    websocketConnection.close();
    setError(true);
    setErrorMessage(
      "We're having trouble connecting to the AI assistant. Trying to reconnect automatically..."
    );
    scheduleRetry();
  }

  const handleRefresh = () => {
    // Reconnect automatically. Only resend the request when the user is still
    // waiting for a first response; otherwise keep the current suggestions.
    // The error label stays visible until data arrives.
    clearRetry();
    if (isPendingRef.current) {
      setLoading(true);
    }
    // Clear the pending guard so handleSuggest can send after reconnection.
    pendingRequestRef.current = false;
    websocketConnection.close();
    websocketConnection.connect(
      () => {
        if (isPendingRef.current) {
          handleSuggest();
        }
      },
      handleError,
      handleErrorClose
    );
  }

  const handleMessage = (message: string) => {
    const res = JSON.parse(message);

    if (res.ping || res.pong)
      return;

    if (res?.message?.error) {
      console.error("Error received from server:", res.message.error);
      isPendingRef.current = false;
      pendingRequestRef.current = false;
      clearRetry();
      setError(true);
      setErrorMessage(
        "We couldn't get suggestions from the AI assistant right now."
      );
      setLoading(false);
      return;
    }

    // A real server message completes the current request cycle, so later
    // (user-initiated) requests are never blocked by a stale pending flag.
    pendingRequestRef.current = false;

    const processItems = (items: string[]) => {
      return items.map(
        (item: string) => ({ id: randomID(), text: item } as IOption)
      );
    };

    if (res?.message) {
      try {
        const itemsMap: Record<DataType, string[]> = {
          [DataType.ROLES]: res.message.roles,
          [DataType.VALUES]: res.message.values,
          [DataType.PRINCIPLES]: (res.message?.principles || res.message?.Principles),
          [DataType.CODE]: res.message.code_of_conduct,
          [DataType.PURPOSE]: res.message.purposes,
          [DataType.VISION]: res.message.visions,
        };

        const items = itemsMap[dataTypeRef.current];

        // Validation to ensure items is an array of strings
        const isArrayOfStrings = (arr: any[]): arr is string[] => arr.every(item => typeof item === 'string');

        if (!isArrayOfStrings(items)) {
          // Ignore auxiliary/non-conforming messages (status, partial or
          // previous-step payloads) instead of regenerating, which would
          // replace the suggestions currently visible to the user.
          return false;
        }

        if (items) {
          const response: IOption[] = processItems(items);
          isPendingRef.current = false;
          clearRetry();
          retryCountRef.current = 0;
          setItems(response);
          setLoading(false);
          setError(false);
          setErrorMessage("");
          suggestionsShownRef.current = true;
        }
      } catch (e) {}
    }
  };

  const handleError = () => {
    // Only surface a connection error while suggestions are enabled.
    if (!isSwitch) return;

    pendingRequestRef.current = false;
    // Keep the spinner (or the current suggestions) visible and show a message
    // that we're trying to reconnect automatically.
    setError(true);
    setErrorMessage(
      "We're having trouble connecting to the AI assistant. Trying to reconnect automatically..."
    );
    scheduleRetry();
  }

  const handleRegenerate = () => {
    setLoading(true);
    setIsShowMore(false);
    suggestionsShownRef.current = false;

    // Explicit regenerations (button click, new step load) always send a
    // fresh request even if a previous one is still pending.
    pendingRequestRef.current = false;

    if (error) {
      // Force an immediate reconnect; the pending request will be resent
      // automatically once the socket is open again (see handleOpen). The
      // error label stays visible until data arrives.
      isPendingRef.current = true;
      retryCountRef.current = 0;
      clearRetry();
      websocketConnection.close();
      websocketConnection.connect(handleOpen, handleError, handleErrorClose);
    }
    else {
      handleSuggest();
    }
  };

  const handleSelect = (item: IOption) => {
    setSelectedItems((prev) =>
      step >= 3
        ? [item] // Radio button behavior for step 3 or more
        : prev.some((i) => i.id === item.id)
          ? prev.filter((i) => i.id !== item.id) // Remove item
          : [...prev, item] // Add item
    );
  };
  
  const handleApply = () => {
    onSelect(selectedItems, dataType);
    setSelectedItems([]);
  }

  return (
    <MainWrapper>
      <AiSuggestionContianer>
        <Header>
          <CloseBtn onClick={onClose}>
            <img src={MenuExpandLeft} alt="" />
          </CloseBtn>
          
          <HeaderWrap>
            <HeaderTitle>AI Assistant</HeaderTitle>
            <Image src={Stars} />
          </HeaderWrap>
        </Header>

        <SubHeader>
          <SubHeaderTitle>Enable Suggestions</SubHeaderTitle>
          <Switch onChange={handleSwitchToggle} />
        </SubHeader>
      </AiSuggestionContianer>

      <Body>
        {isSwitch && (
          loading ? (
            <Loader
              title={data[step-1].title}
              loading={loading || error}
              error={errorMessage}
            />
          ) : (
            <>
              {error && errorMessage && (
                <LoaderError $animate={!!error} $animateDelay={0}>
                  {errorMessage}
                </LoaderError>
              )}
              <ChipsWrapper>
                {items.slice(0, isShowMore ? items.length : 5).map(item =>
                  <Option
                    data={item}
                    isSelected={selectedItems.some((i) => i.id === item.id)}
                    key={item.id}
                    type={(step === 1 || step === 2) ? 'checkbox' : 'radio'}
                    onChange={handleSelect}
                  />
                )}
              </ChipsWrapper>

              <Footer>
                {(step > 2 || isShowMore) ? null : <Button styleType="link-primary" onClick={() => setIsShowMore(true)}>Show More</Button>}

                {(step <= 2 && isShowMore) || (step > 2 && step !== 7) ? (
                  <RegGernerateButton onClick={handleRegenerate}>
                    <Image src={ReLoad} /> Regenerate
                  </RegGernerateButton>
                ) : null}

                <Button onClick={handleApply} disabled={!selectedItems.length}>Apply</Button>
              </Footer>

              {step === 7 && (
                <Wrapper>
                  <RegGernerateButton>Copy</RegGernerateButton>
                  <RegGernerateButton>Apply</RegGernerateButton>
                </Wrapper> 
              )}
            </>
        ))}
      </Body>
    </MainWrapper>
  );
};

export default AIHelper;
